import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Loader2, Save, RotateCcw, Plus, Trash2, ArrowUp, ArrowDown, Undo2, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

/**
 * AdminOurStoryManager
 *
 * Edits the /our-story page content: eyebrow, title, image badge,
 * paragraphs (CRUD + reorder), pull-quote, and signature. Design and
 * layout of the public component are preserved.
 */
const AdminOurStoryManager = ({ authHeader }) => {
  const [initial, setInitial] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/our-story/content`);
      setInitial(clone(res.data || {}));
      setContent(clone(res.data || {}));
    } catch { setError('Could not load current content.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => (
    content && initial && JSON.stringify(content) !== JSON.stringify(initial)
  ), [content, initial]);

  const patch = (fn) => setContent((prev) => { const n = clone(prev); fn(n); return n; });

  const save = async () => {
    if (!dirty) return;
    setSaving(true); setError('');
    try {
      const res = await axios.put(`${API}/admin/our-story/content`, content, { headers: authHeader() });
      setInitial(clone(res.data));
      setContent(clone(res.data));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/our-story/content/reset`, {}, { headers: authHeader() });
      setInitial(clone(res.data));
      setContent(clone(res.data));
      setResetOpen(false);
    } catch { setError('Failed to reset content.'); }
    finally { setResetting(false); }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file again re-fires
    if (!file) return;
    setUploading(true); setUploadError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/our-story/photo`, form, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      const nextUrl = res.data?.photoUrl;
      if (!nextUrl) throw new Error('No photoUrl in response');
      // The upload endpoint already persisted photoUrl on the server — reflect
      // that in local state so `dirty` stays false and the preview refreshes.
      setInitial((prev) => ({ ...(prev || {}), photoUrl: nextUrl }));
      setContent((prev) => ({ ...(prev || {}), photoUrl: nextUrl }));
    } catch (err) {
      console.error('Upload failed', err);
      setUploadError(err?.response?.data?.detail || 'Upload failed. Please try a different image.');
    } finally {
      setUploading(false);
    }
  };

  // Resolve preview URL — API paths get the backend prefix; static paths are served by the frontend.
  const resolvePreview = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
    return url; // e.g. /images/our-story-hands.webp
  };

  if (loading || !content) {
    return <div className="bg-white rounded-xl p-10 flex items-center justify-center text-[#5A4A4A] gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading Our Story…
    </div>;
  }

  const paras = content.paragraphs || [];
  const setPara = (i, val) => patch((d) => { d.paragraphs[i] = val; });
  const movePara = (i, dir) => patch((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.paragraphs.length) return;
    [d.paragraphs[i], d.paragraphs[j]] = [d.paragraphs[j], d.paragraphs[i]];
  });
  const removePara = (i) => patch((d) => { d.paragraphs.splice(i, 1); });
  const addPara = () => patch((d) => { d.paragraphs = d.paragraphs || []; d.paragraphs.push('New paragraph — write your love-story chapter here.'); });

  return (
    <div className="space-y-4" data-testid="admin-our-story">
      <div className="bg-white rounded-xl shadow-sm p-4 sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-[#3B2F2F]">Our Story</h2>
          <p className="text-xs text-[#8A7A7A]">Edits go live on the public /our-story page.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setResetOpen(true)} className="text-xs text-[#8A7A7A] hover:text-red-500 inline-flex items-center gap-1" data-testid="ourstory-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset defaults
          </button>
          {dirty && (
            <button onClick={() => setContent(clone(initial))} disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]"
              data-testid="ourstory-revert-btn">
              <Undo2 className="w-3.5 h-3.5" /> Discard
            </button>
          )}
          <Button onClick={save} disabled={!dirty || saving} className="bg-[#C8A951] hover:bg-[#B09240] text-white disabled:opacity-60" data-testid="ourstory-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {savedFlash ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}

      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Eyebrow (script line)</label>
            <Input value={content.eyebrow || ''} onChange={(e) => patch((d) => { d.eyebrow = e.target.value; })} data-testid="ourstory-eyebrow" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Title</label>
            <Input value={content.title || ''} onChange={(e) => patch((d) => { d.title = e.target.value; })} data-testid="ourstory-title" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Image badge label</label>
          <Input value={content.imageBadge || ''} onChange={(e) => patch((d) => { d.imageBadge = e.target.value; })} placeholder="Where It All Began" data-testid="ourstory-badge" />
        </div>

        {/* Cover photo uploader */}
        <div className="border border-[#E5E0D8] rounded-lg p-4" data-testid="ourstory-photo-card">
          <p className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-3 flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5 text-[#C8A951]" /> Cover Photo
          </p>
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-start">
            <div className="relative rounded-lg overflow-hidden bg-[#F8F5EF] border border-[#E5E0D8] aspect-[4/5]">
              {content.photoUrl ? (
                <img
                  src={resolvePreview(content.photoUrl)}
                  alt="Our Story cover preview"
                  className="w-full h-full object-cover"
                  data-testid="ourstory-photo-preview"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#8A7A7A] text-sm">
                  No photo yet
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-[#C8A951]" />
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-[#5A4A4A] mb-3">
                This photo appears next to the story on the public /our-story page. Uploading a new
                one takes effect immediately — no redeploy required. JPG, PNG, HEIC, or WEBP up to 40 MB.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="hidden"
                onChange={onFilePicked}
                data-testid="ourstory-photo-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white disabled:opacity-60"
                data-testid="ourstory-photo-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                {uploading ? 'Uploading…' : content.photoUrl ? 'Replace photo' : 'Upload photo'}
              </Button>
              {uploadError && <p className="text-red-500 text-xs mt-2" data-testid="ourstory-photo-error">{uploadError}</p>}
              <p className="text-[11px] text-[#8A7A7A] mt-3 break-all">
                Current URL: <code className="text-[#3B2F2F]">{content.photoUrl || '—'}</code>
              </p>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A]">Paragraphs</label>
            <Button onClick={addPara} className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white h-8 px-3" data-testid="ourstory-add-para">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add paragraph
            </Button>
          </div>
          <ul className="space-y-3">
            {paras.map((p, i) => (
              <li key={i} className="grid grid-cols-[1fr_auto] gap-2 items-start" data-testid={`ourstory-para-row-${i}`}>
                <Textarea rows={4} value={p} onChange={(e) => setPara(i, e.target.value)} data-testid={`ourstory-para-${i}`} />
                <div className="flex flex-col items-center gap-1">
                  <button onClick={() => movePara(i, -1)} disabled={i === 0} className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`ourstory-para-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                  <button onClick={() => movePara(i, 1)} disabled={i === paras.length - 1} className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30" data-testid={`ourstory-para-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => removePara(i)} className="p-1.5 rounded-md text-red-500 hover:bg-red-50" data-testid={`ourstory-para-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Pull-quote (italic, gold border)</label>
          <Textarea rows={4} value={content.quote || ''} onChange={(e) => patch((d) => { d.quote = e.target.value; })} data-testid="ourstory-quote" />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Signature (cursive line)</label>
          <Input value={content.signature || ''} onChange={(e) => patch((d) => { d.signature = e.target.value; })} placeholder="Forever & Always" data-testid="ourstory-signature" />
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Our Story to defaults?</DialogTitle>
            <DialogDescription>This restores the original text and cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button variant="destructive" onClick={resetDefaults} disabled={resetting} data-testid="ourstory-reset-confirm">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOurStoryManager;
